import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { api } from "../../common/setupApi";
import { isObjectEmpty } from "../../common/utils";

export const getDatepicker = createAsyncThunk(
  "datepicker/get",
  async (_, { rejectWithValue }) => {
    return api
      .get("sessions-list/format/")
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        return rejectWithValue(err.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectDatepicker(getState());
      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

const initialState = {
  status: "idle",
  data: {},
  error: null,
  selectedDate: String(new Date()),
};

const datepickerSlice = createSlice({
  name: "datepicker",
  initialState,
  reducers: {
    changeDate: (state, action) => {
      state.selectedDate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDatepicker.pending, (state) => {
        state.status = "pending";
        state.error = null;
      })
      .addCase(getDatepicker.fulfilled, (state, action) => {
        state.status = "fulfilled";
        state.data = action.payload;
      })
      .addCase(getDatepicker.rejected, (state, action) => {
        state.status = "rejected";
        state.error = action.payload;
      })
      .addCase("auth/logout", () => initialState);
  },
});

export const datepickerActions = datepickerSlice.actions;

const _selectDatepicker = (state) => state.datepicker;

export const selectDatepicker = createSelector(
  [(state) => _selectDatepicker(state)],
  (datepicker) => {
    const data = datepicker.data;
    const status = datepicker.status;
    const error = datepicker.error;
    const isLoading = status === "pending";
    return {
      events: data.sessions || {},
      competitions: data.competitions || {},
      status,
      error,
      isLoading,
    };
  }
);

// https://redux.js.org/usage/deriving-data-selectors#writing-memoized-selectors-with-reselect
const selectDatePickerDate = (state) => state.datepicker.selectedDate;
export const selectSelectedDate = createSelector(
  [selectDatePickerDate],
  (date) => new Date(date)
);

export const selectUserHasCreatedEvent = createSelector(
  [(state) => selectDatepicker(state)],
  (datepicker) => {
    const sessions = datepicker.events;
    const isSessionCreated = !isObjectEmpty(sessions);

    const matchs = datepicker.competitions;
    const isMatchCreated = !isObjectEmpty(matchs);

    return isSessionCreated || isMatchCreated;
  }
);

export default datepickerSlice.reducer;
